import * as React from 'react';
import { ActionGroup, Button, Form, FormGroup, FormHelperText, TextInput } from '@patternfly/react-core';
import SendNotificationField from './SendNotificationField';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import ValidatedType from '@app/Type/ValidatedType';
import User from '@app/Interface/UserInterface';
import { UserContext } from '@app/Context/UserContext';
import UserAuthentication from '@app/Security/UserAuthentication';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';

interface UpdateNotificationFieldProps {
    type: "Email" | "Text";
    currentUser: User;
    handleEditSettingButton: (value: boolean, type: "Email" | "Text") => void;
}

const UpdateNotificationField: React.FC<UpdateNotificationFieldProps> = ({ type, currentUser, handleEditSettingButton }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const fieldId = type === "Email" ? "sendEmailNotifications" : "sendSmsNotifications";

    const initialChecked = type === "Email" ? currentUser.sendEmailNotifications : currentUser.sendSmsNotifications;

    const [sendNotification, setSendNotification] = React.useState(initialChecked);

    const initialValue = type === "Email" ? currentUser.notificationEmailAddress : currentUser.notificationPhoneNumber;

    const [sendNotificationValue, setSendNotificationValue] = React.useState(initialValue);

    const [validValue, setValidValue] = React.useState<ValidatedType>("default");

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const { userDispatch } = React.useContext(UserContext);

    /**
     * Handler to change the isChecked state for sending a notification
     * @param checked 
     * @param event 
     */
    const handleChange = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        // handle change.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const fieldId = event.currentTarget.id;

        setSendNotification(checked);

    };

    const handleSubmit = async () => {

        console.group("UpdateNotificationField");
        console.info("handleSubmit");
        console.info("sendNotification: ", sendNotification);
        console.info("sendNotificationValue: ", sendNotificationValue);
        console.groupEnd();

        setIsFormProcessing(true);

        const isValid = validForm();

        if (isValid) {

            // update user context
            const updatedUser = {...currentUser};

            if (type === "Email") {
                updatedUser.sendEmailNotifications = sendNotification;
                updatedUser.notificationEmailAddress = sendNotificationValue;
            } else {
                updatedUser.sendSmsNotifications = sendNotification;
                updatedUser.notificationPhoneNumber = sendNotificationValue;    
            }

            userDispatch({
                type: "UPDATE",
                payload: updatedUser
            });

            // send async request 
            const responseCode = await UserAuthentication.saveUser(updatedUser);

            let notificationType: ToastNotificationType = ToastNotificationType.Default;

            let toastTitle = "";

            let tastDescription = "";

            switch (responseCode) {

                case 200:
                    notificationType = ToastNotificationType.Success;
                    toastTitle = "Success";
                    tastDescription = `${type} notification setting was updated.`;
                    
                    userDispatch({
                        type: "UPDATE",
                        payload: updatedUser
                    });
                    
                    break;

                case 400:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Invalid input";
                    tastDescription = "Please try again or contact support if the issue persists.";
                    break;

                case 404:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Resource not found";
                    tastDescription = "Endpoint to save notification settings is invalid, try again or contact support.";
                    break;

                case 422:
                    notificationType = ToastNotificationType.Danger;
                    toastTitle = "Bad user data";
                    tastDescription = "Notification data error";
                    break;

                default:
                    notificationType = ToastNotificationType.Warning;
                    toastTitle = "Unknown error";
                    tastDescription = "Unable to save notification data, contact support.";
                    break;
            }

            CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);

        }

        setIsFormProcessing(false);

    };

    const validForm = (): boolean => {

        let valid = true;

        const isEmpty = sendNotificationValue.trim().length === 0;

        if (sendNotification && isEmpty) {

            valid = false;

            setValidValue("error");

        }

        if (type === "Email" && isValidEmail(sendNotificationValue) === false) {

            valid = false;

            setValidValue("error");

        }

        if (type === "Text" && isValidPhone(sendNotificationValue) === false) {

            valid = false;

            setValidValue("error");

        }

        return valid;

    };

    /**
     * TODO: finish stub for checking email
     * 
     * @param value 
     * @returns 
     */
    const isValidEmail = (value: string): boolean => {

        if (typeof value === "string") {
            return true;
        }

        return false;

    };

    /**
     * TODO: finish Stub to check for valid phone number 
     * 
     * @param value 
     * @returns 
     */
    const isValidPhone = (value: string): boolean => {

        if (typeof value === "string") {
            return true;
        }

        return false;

    };

    React.useEffect(() => {

        return () => {
            
            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);
    
    return (
        <Form onSubmit={handleSubmit}>
            <FormGroup
                fieldId={fieldId}
            >
                <SendNotificationField
                    type={type}
                    fieldId={fieldId}
                    isChecked={sendNotification}
                    notificationValue={sendNotificationValue}
                    handleChange={handleChange}
                    handleEditSettingButton={handleEditSettingButton}
                    showEditButton={false}
                />
            </FormGroup>
            <FormGroup
                label={`${type} Notifications will be sent to`}
                type="string"
                helperText={
                    // eslint-disable-next-line react/jsx-no-undef
                    <FormHelperText icon={<ExclamationCircleIcon />} isHidden={validValue !== "error"}>Please enter {type}.</FormHelperText>
                }
                helperTextInvalid={`Enter a valid ${type} notification value`}
                helperTextInvalidIcon={<ExclamationCircleIcon />}
                fieldId="sendNotificationValue"
                validated={validValue}
                isRequired={sendNotification}
            >
                <TextInput
                    isRequired={sendNotification}
                    validated={validValue}
                    value={sendNotificationValue}
                    id="sendNotificationValue"
                    aria-describedby="sendNotificationValue"
                    onChange={(newValue: string) => setSendNotificationValue(newValue)}
                />
            </FormGroup>
            <ActionGroup>
                <Button variant="primary" onClick={handleSubmit} isLoading={isFormProcessing}>
                    {isFormProcessing ? "Saving" : "Save"}
                </Button>
                <Button variant="link" onClick={() => handleEditSettingButton(false, type)}>
                    Back
                </Button>
            </ActionGroup>
        </Form>
    );

};

export default UpdateNotificationField;